/**
 * Broker / Company-broker Statistics Page
 *
 * Shows the caller their own property statistics — per-property views (raw +
 * unique), WhatsApp clicks, phone clicks, inquiries — plus a top-line summary.
 * Each broker (independent or company-affiliated) sees only their own listings;
 * the same /api/events/broker/stats endpoint serves both, filtering by
 * OR(ownerId, agentId) for the authenticated user.
 *
 * Aggregated company-wide and admin-wide views live elsewhere (company
 * dashboard + admin panel) and use different endpoints.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useAuth } from '../../../hooks/useAuth';
import { apiRequest, ApiError } from '../../../utils/apiClient';
import SegmentedControl from '../../../components/base/SegmentedControl';
import FigureStrip, { type Figure } from '../../../components/base/FigureStrip';
import { formatAmount } from '../../../utils/numberFormat';
import { BrandRule } from '../../../components/brand/BrandRule';

type Period = 'day' | 'week' | 'month' | 'all';

interface PropertyStats {
  id: string;
  title: string;
  slug: string | null;
  price: number;
  listingType: string | null;
  status: string;
  cityNameEn: string | null;
  cityNameAr: string | null;
  imageUrl: string | null;
  createdAt: string;
  // All-time denormalized counters (independent of period)
  allTimeViews: number;
  allTimeFavorites: number;
  allTimeInquiries: number;
  // Period-filtered event counts
  views: number;
  uniqueViewers: number;
  whatsappClicks: number;
  phoneClicks: number;
  emailClicks: number;
  inquiries: number;
  favorites: number;
  shares: number;
}

interface StatsResponse {
  data: {
    period: Period;
    startDate: string | null;
    summary: {
      totalProperties: number;
      totalViews: number;
      uniqueViewers: number;
      totalWhatsappClicks: number;
      totalPhoneClicks: number;
      totalEmailClicks: number;
      totalInquiries: number;
      totalFavorites: number;
    };
    properties: PropertyStats[];
  };
}

// Threshold above which the "Hot listing" badge surfaces in the per-property
// row. Tuned conservatively — adjust once we have a feel for traffic.
const HOT_LISTING_VIEW_THRESHOLD = 500;

const TH = 'px-3 py-3 text-center text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]';
const TD = 'px-3 py-3 text-center tabular-nums';

function formatNumber(value: number): string {
  return formatAmount(value);
}

function formatPrice(value: number): string {
  return formatAmount(value, { maximumFractionDigits: 0 });
}

export default function StatisticsPage() {
  const { t, i18n } = useTranslation('dashboard');
  const { session } = useAuth();
  const token = session?.token;
  const isRTL = i18n.language === 'ar';

  const [period, setPeriod] = useState<Period>('week');
  const [stats, setStats] = useState<StatsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest<StatsResponse>(
        `/events/broker/stats?period=${period}`,
        { token },
      );
      setStats(response.data);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : isRTL
          ? 'تعذر تحميل الإحصائيات.'
          : 'Failed to load statistics.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token, period, isRTL]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const periodOptions: { value: Period; label: string }[] = useMemo(
    () => [
      { value: 'day', label: isRTL ? 'اليوم' : 'Today' },
      { value: 'week', label: isRTL ? 'آخر 7 أيام' : 'Last 7 days' },
      { value: 'month', label: isRTL ? 'آخر 30 يوم' : 'Last 30 days' },
      { value: 'all', label: isRTL ? 'كل الوقت' : 'All time' },
    ],
    [isRTL],
  );

  // The top line. Labels stay up while the numbers load, so the page keeps its
  // shape when the period changes.
  const figures = useMemo<Figure[]>(() => {
    const s = stats?.summary;
    const n = (value: number | undefined) => formatNumber(value ?? 0);
    return [
      {
        key: 'views',
        label: isRTL ? 'المشاهدات' : 'Views',
        value: n(s?.totalViews),
        note: s ? `${n(s.uniqueViewers)} ${isRTL ? 'زائر فريد' : 'unique'}` : undefined,
      },
      { key: 'whatsapp', label: isRTL ? 'نقرات واتساب' : 'WhatsApp clicks', value: n(s?.totalWhatsappClicks) },
      { key: 'phone', label: isRTL ? 'نقرات الاتصال' : 'Phone clicks', value: n(s?.totalPhoneClicks) },
      { key: 'inquiries', label: isRTL ? 'الاستفسارات' : 'Inquiries', value: n(s?.totalInquiries) },
      { key: 'properties', label: isRTL ? 'العقارات' : 'Properties', value: n(s?.totalProperties) },
    ];
  }, [stats, isRTL]);

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <header className="border-b border-[var(--pv-border-subtle)]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <nav aria-label={isRTL ? 'مسار التنقل' : 'Breadcrumb'} className="flex items-center gap-2 text-[0.875rem] text-[var(--pv-color-text-muted)]">
            <Link to="/dashboard" className="transition-colors hover:text-[var(--pv-color-primary)]">
              {isRTL ? 'لوحة التحكم' : 'Dashboard'}
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-[var(--pv-color-text)]">{isRTL ? 'الإحصائيات' : 'Statistics'}</span>
          </nav>
          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-[1.5rem] font-bold text-[var(--pv-color-primary)] sm:text-[1.75rem]">
                {isRTL ? 'إحصائيات عقاراتي' : 'My Property Statistics'}
              </h1>
              <BrandRule className="mt-3" />
              <p className="mt-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {isRTL
                  ? 'مشاهدات، نقرات واتساب، اتصالات، واستفسارات لكل عقار من عقاراتك.'
                  : 'Views, WhatsApp clicks, phone clicks, and inquiries for each of your listings.'}
              </p>
            </div>
            <SegmentedControl
              size="sm"
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              ariaLabel={isRTL ? 'الفترة' : 'Period'}
              className="self-start sm:self-auto"
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && !loading ? (
          <div role="alert" className="rounded-[16px] border border-[var(--pv-border-soft)] px-6 py-8 text-center">
            <p className="text-[0.9375rem] text-[var(--pv-color-text)]">{error}</p>
            <button
              type="button"
              onClick={fetchStats}
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border border-[var(--pv-color-primary)] px-4 text-[0.875rem] font-semibold text-[var(--pv-color-primary)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              {isRTL ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        ) : (
          <>
            <FigureStrip figures={figures} loading={loading || !stats} />

            {loading || !stats ? (
              <div aria-hidden="true" className="mt-10 space-y-2">
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i} className="h-16 animate-pulse rounded-lg bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
                ))}
              </div>
            ) : stats.properties.length === 0 ? (
              <div className="mt-10 rounded-[16px] border border-[var(--pv-border-soft)] px-6 py-10 text-center">
                <p className="text-[1rem] font-semibold text-[var(--pv-color-text)]">
                  {isRTL ? 'لا توجد عقارات بعد' : 'No properties yet'}
                </p>
                <p className="mt-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                  {isRTL ? 'أضف عقاراً أولاً لرؤية الإحصائيات هنا.' : 'Add a property first to see statistics here.'}
                </p>
                <Link
                  to="/properties/new"
                  className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-[var(--pv-color-primary)] px-5 text-[0.9375rem] font-semibold text-white transition-colors hover:bg-[#003930] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30 focus-visible:ring-offset-2"
                >
                  <i className="ri-add-line" aria-hidden="true" />
                  {isRTL ? 'إضافة عقار' : 'Add property'}
                </Link>
              </div>
            ) : (
              <section className="mt-10" aria-labelledby="stats-properties">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 id="stats-properties" className="text-[1.125rem] font-semibold text-[var(--pv-color-primary)]">
                    {isRTL ? 'تفاصيل العقارات' : 'Per-property breakdown'}
                  </h2>
                  <span className="text-[0.875rem] tabular-nums text-[var(--pv-color-text-muted)]">
                    {isRTL
                      ? `${formatNumber(stats.properties.length)} عقار`
                      : `${formatNumber(stats.properties.length)} listing${stats.properties.length === 1 ? '' : 's'}`}
                  </span>
                </div>

                <div className="mt-3 overflow-x-auto rounded-[16px] border border-[var(--pv-border-soft)]">
                  <table className="w-full min-w-[46rem] border-collapse text-[0.9375rem] text-[var(--pv-color-text)]">
                    <thead className="bg-[var(--pv-color-surface-sunken)]">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-start text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]">{isRTL ? 'العقار' : 'Property'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'مشاهدات' : 'Views'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'فريد' : 'Unique'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'واتساب' : 'WhatsApp'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'اتصال' : 'Phone'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'استفسارات' : 'Inquiries'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'الكل (دائم)' : 'All time'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.properties.map((p) => {
                        const isHot = p.allTimeViews >= HOT_LISTING_VIEW_THRESHOLD;
                        const cityName = isRTL ? p.cityNameAr || p.cityNameEn : p.cityNameEn || p.cityNameAr;
                        return (
                          <tr key={p.id} className="border-t border-[var(--pv-border-subtle)] transition-colors hover:bg-[var(--pv-color-surface-soft)]">
                            <td className="px-4 py-3">
                              <Link to={`/property/${p.slug || p.id}`} className="group flex items-center gap-3">
                                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-[var(--pv-color-surface-sunken)]">
                                  {p.imageUrl ? (
                                    <img src={p.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-[var(--pv-color-text-subtle)]">
                                      <i className="ri-image-line text-xl" aria-hidden="true" />
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="max-w-[200px] truncate font-medium transition-colors group-hover:text-[var(--pv-color-primary)]">
                                      {p.title}
                                    </span>
                                    {isHot && (
                                      <span
                                        className="rounded-full bg-[#FDE8E7] px-2 py-0.5 text-[0.75rem] font-semibold text-[#C0322A]"
                                        title={
                                          isRTL
                                            ? `عقار شائع - ${formatNumber(p.allTimeViews)} مشاهدة`
                                            : `Hot - ${formatNumber(p.allTimeViews)} all-time views`
                                        }
                                      >
                                        {isRTL ? 'شائع' : 'Hot'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-0.5 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
                                    {cityName && <span>{cityName}</span>}
                                    {p.price > 0 && (
                                      <>
                                        {cityName && <span className="mx-1.5">·</span>}
                                        <span>{formatPrice(p.price)} {t('common:currency.short')}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            </td>
                            <td className={TD}>{formatNumber(p.views)}</td>
                            <td className={`${TD} text-[var(--pv-color-text-muted)]`}>{formatNumber(p.uniqueViewers)}</td>
                            <td className={TD}>{formatNumber(p.whatsappClicks)}</td>
                            <td className={TD}>{formatNumber(p.phoneClicks)}</td>
                            <td className={TD}>{formatNumber(p.inquiries)}</td>
                            <td className={`${TD} text-[var(--pv-color-text-muted)]`}>{formatNumber(p.allTimeViews)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <p className="mt-4 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
              {isRTL
                ? 'يتم تسجيل المشاهدات تلقائياً عند فتح صفحة العقار. نقرات واتساب والاتصال تُسجَّل عند النقر على الأزرار.'
                : 'Views are recorded automatically when a property page is opened. WhatsApp and phone clicks are recorded when those buttons are tapped.'}
            </p>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
